<script setup>
import PostTable from '@frontJs/components/tables/projectManagement/smo/PostTable.vue';
import TenantReportPostTable from '@frontJs/components/tables/projectManagement/smo/tenantReports/PostTable.vue';
import ProjectManagementLayout from '@frontJs/layouts/ProjectManagementLayout.vue';
import { Head } from '@inertiajs/vue3';
import AlertBox from '@js/components/alerts/AlertBox.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { useRoute } from '@js/utils/route';
import { h, onMounted, ref } from 'vue';

const { tenant, isPlatformConfigured } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    statuses: {
        type: Array,
        required: false,
        default: () => [],
    },
    isPlatformConfigured: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const loading = ref(true);

const selectedTab = ref('scheduled');

const tabStatuses = {
    draft: ['draft'],
    scheduled: ['scheduled', 'publishing'],
    published: ['published'],
    failed: ['failed'],
    canceled: ['canceled'],
};

const data = ref({
    draft: 0,
    scheduled: 0,
    published: 0,
    failed: 0,
    canceled: 0,
});

function updateData() {
    loading.value = true;

    const tabs = Object.keys(tabStatuses);

    Promise.all(
        tabs.map((tab) =>
            axios.post(
                route('front.projectManagement.smo.postPostCount', {
                    tenant: tenant.id,
                }),
                { status: tabStatuses[tab] },
            ),
        ),
    )
        .then((responses) => {
            tabs.forEach((tab, index) => {
                data.value[tab] = responses[index].data.count;
            });
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

onMounted(() => {
    if (tenant) {
        updateData();
    }
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
    <Head>
        <title>Publications - SMO - Gestion de projet - Datakepler</title>
    </Head>

    <PageTitle :tenant-name="tenant ? tenant.name : undefined">
        Gestion de projet - SMO - Publications
    </PageTitle>

    <template v-if="tenant">
        <TabLayout
            v-model:selected-tab="selectedTab"
            :tabs="{
                draft: {
                    title: 'Brouillons',
                    total: data.draft,
                    content: h(PostTable, {
                        tenant: tenant,
                        status: tabStatuses.draft,
                        callback: () => updateData(),
                    }),
                },
                scheduled: {
                    title: 'Programmées',
                    total: data.scheduled,
                    content: h(PostTable, {
                        tenant: tenant,
                        status: tabStatuses.scheduled,
                        callback: () => updateData(),
                    }),
                },
                published: {
                    title: 'Publiées',
                    total: data.published,
                    content: h(PostTable, {
                        tenant: tenant,
                        status: tabStatuses.published,
                        callback: () => updateData(),
                    }),
                },
                failed: {
                    title: 'En échec',
                    total: data.failed,
                    content: h(PostTable, {
                        tenant: tenant,
                        status: tabStatuses.failed,
                        callback: () => updateData(),
                    }),
                },
                canceled: {
                    title: 'Annulées',
                    total: data.canceled,
                    content: h(PostTable, {
                        tenant: tenant,
                        status: tabStatuses.canceled,
                        callback: () => updateData(),
                    }),
                },
            }"
            :loading="loading"
        >
            <template #content-header>
                <AlertBox v-if="!isPlatformConfigured" type="warning">
                    Le service externe Facebook n'est pas configuré pour ce
                    client. Les publications ne pourront pas être créées tant
                    que la page Facebook n'est pas renseignée dans la fiche
                    client.
                </AlertBox>
            </template>
        </TabLayout>
    </template>

    <TenantReportPostTable v-else />
</template>
